import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SelectField, TextField } from "@/components/Field";
import { db } from "@/services/firebase";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useInstitutionUsers } from "@/lib/hooks/useCollection";
import { call, CALLABLES } from "@/services/callables";
import { useCallable } from "@/lib/hooks/useCallable";
import { FAULT_TONE, faultOf, type CoverageRow } from "@/features/legal/coverage";
import { formatDate, formatDateTime, formatNumber } from "@/lib/format";
import { institutionState } from "./zone";
import {
  licenceProblems,
  parseSeats,
  STUDENT_SEATS_MAX,
  TIER_MAX_TEACHERS,
  TIERS,
} from "./licence";
import type { Institution, LicenceTier, User } from "@/types";

/**
 * One institution.
 *
 * Six tabs. The licence tab is the important one and says why: for a school
 * paying by bank transfer, somebody setting a date here **is** the billing
 * system. There is no invoicing product behind it, and pretending otherwise
 * would make the screen look less consequential than it is.
 */

type Tab = "overview" | "licence" | "usage" | "users" | "legal" | "danger";

const TABS: readonly { id: Tab; labelKey: string }[] = [
  { id: "overview", labelKey: "institution.tab.overview" },
  { id: "licence", labelKey: "institution.tab.licence" },
  { id: "usage", labelKey: "institution.tab.usage" },
  { id: "users", labelKey: "institution.tab.users" },
  { id: "legal", labelKey: "institution.tab.legal" },
  { id: "danger", labelKey: "institution.tab.danger" },
];

export function InstitutionPage() {
  const { institutionId = "" } = useParams();
  const t = useT();
  const [tab, setTab] = useState<Tab>("overview");
  const [institution, setInstitution] = useState<Institution | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!institutionId) return;
    return onSnapshot(
      doc(db(), "institutions", institutionId),
      (snapshot) => {
        setInstitution(snapshot.exists() ? (snapshot.data() as Institution) : undefined);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [institutionId]);

  if (loading) {
    return (
      <SectionShell titleKey="institutions.title">
        <p className="text-sm text-ink-muted">{t("common.loading")}</p>
      </SectionShell>
    );
  }

  if (!institution) {
    return (
      <SectionShell titleKey="institutions.title">
        <p className="text-ink-muted">{t("error.notFound.body")}</p>
      </SectionShell>
    );
  }

  return (
    <SectionShell titleKey="dashboard.welcome" titleParams={{ name: institution.name }}>
      <h1 className="sr-only">{institution.name}</h1>

      <div role="tablist" className="flex flex-wrap gap-1 border-b border-paper-deep">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={entry.id === tab}
            onClick={() => setTab(entry.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
              entry.id === tab
                ? "border-cue-green font-medium text-ink"
                : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            {t(entry.labelKey)}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "overview" ? <Overview institution={institution} /> : null}
        {tab === "licence" ? <Licence institution={institution} /> : null}
        {tab === "usage" ? <Usage institution={institution} /> : null}
        {tab === "users" ? <Users institution={institution} /> : null}
        {tab === "legal" ? <Legal institution={institution} /> : null}
        {tab === "danger" ? <Danger institution={institution} /> : null}
      </div>
    </SectionShell>
  );
}

function Overview({ institution }: { institution: Institution }) {
  const t = useT();
  const { locale } = useI18n();
  const state = institutionState(institution, Date.now());

  return (
    <div>
    <dl className="grid max-w-2xl grid-cols-[14rem_1fr] gap-y-2 text-sm">
      <dt className="text-ink-muted">{t("institutions.column.country")}</dt>
      <dd className="text-ink">{institution.country}</dd>

      <dt className="text-ink-muted">{t("institutions.column.zone")}</dt>
      <dd className="text-ink">{t(`zone.${institution.zone}`)}</dd>

      <dt className="text-ink-muted">{t("institutions.column.segment")}</dt>
      <dd className="text-ink">{t(`institutions.segment.${institution.segment}`)}</dd>

      <dt className="text-ink-muted">{t("institutions.column.status")}</dt>
      <dd className="text-ink">{t(`institutions.state.${state}`)}</dd>

      <dt className="text-ink-muted">{t("institution.overview.created")}</dt>
      <dd className="text-ink">{formatDateTime(locale, institution.createdAt)}</dd>

      <dt className="text-ink-muted">{t("institution.overview.billingEmail")}</dt>
      <dd className="text-ink">{institution.billing.billingEmail}</dd>

      <dt className="text-ink-muted">{t("institution.overview.vat")}</dt>
      <dd className="text-ink">
        {institution.billing.vatId ? (
          <>
            {institution.billing.vatId}
            {institution.billing.vatIdValidatedAt ? (
              <span className="ml-2 text-ink-muted">
                {t("institution.overview.vatValidated", {
                  date: formatDate(locale, institution.billing.vatIdValidatedAt),
                })}
              </span>
            ) : null}
          </>
        ) : (
          t("institution.overview.vatNone")
        )}
      </dd>

      <dt className="text-ink-muted">{t("billing.plan.title")}</dt>
      <dd className="text-ink">
        {t(
          institution.billing.manualBilling
            ? "institution.overview.manualBilling"
            : "institution.overview.stripe"
        )}
      </dd>
    </dl>
    <OwnerAccess institution={institution} />
    </div>
  );
}

/**
 * The owner's way in.
 *
 * There is no password on this screen and never was one worth writing down:
 * the owner chooses theirs through a link the backend mails. What the console
 * can do is send that mail again — the one action here that can put a key to
 * the institution in front of whoever holds the address, which is why it is
 * an admin's and is written to the audit log.
 */
function OwnerAccess({ institution }: { institution: Institution }) {
  const t = useT();
  const { success, error, showError } = useToast();
  const { rows } = useInstitutionUsers<User>(institution.id);
  const [sendingTo, setSendingTo] = useState<string | undefined>(undefined);

  const owners = rows.filter((user) => user.role === "owner" && !user.disabled);

  return (
    <section className="mt-10 max-w-2xl border-t border-paper-deep pt-6">
      <h2 className="font-display text-lg text-ink">{t("institution.access.title")}</h2>
      <p className="mt-2 text-sm text-ink-soft">{t("institution.access.body")}</p>

      {owners.length === 0 ? (
        <p className="mt-4 text-sm text-ink-muted">{t("institution.access.noOwner")}</p>
      ) : (
        <ul className="mt-4 flex flex-col gap-3">
          {owners.map((owner) => (
            <li key={owner.uid} className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm">
                <span className="text-ink">{owner.displayName}</span>
                <span className="ml-2 text-ink-muted">{owner.email ?? t("common.none")}</span>
              </span>
              <Button
                variant="secondary"
                busy={sendingTo === owner.uid}
                disabled={sendingTo !== undefined || !owner.email}
                onClick={async () => {
                  setSendingTo(owner.uid);
                  try {
                    const result = await call<{ uid: string }, { sent: boolean; email: string }>(
                      CALLABLES.sendSignInLink,
                      { uid: owner.uid }
                    );
                    // A mail that did not go is an error that stays on screen:
                    // the link exists, the person does not have it.
                    if (result.sent) success("institution.access.sent", { email: result.email });
                    else error("institution.access.notSent", { email: result.email });
                  } catch (caught) {
                    showError(caught);
                  } finally {
                    setSendingTo(undefined);
                  }
                }}
              >
                {t("institution.access.send")}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Setting a licence by hand.
 *
 * For a school paying by bank transfer this is the entire billing path: the
 * money arrives, and somebody sets the date. The panel says so, because a
 * screen that looks like an administrative convenience gets treated like one.
 */
function Licence({ institution }: { institution: Institution }) {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();

  const [tier, setTier] = useState<LicenceTier>(institution.licenceTier);
  const [seats, setSeats] = useState(institution.teacherSeats);
  // Empty for a licence written before the student limit existed. The field
  // says so, and the button stays off until a number is chosen.
  const [students, setStudents] = useState(
    institution.studentSeats === undefined ? "" : String(institution.studentSeats)
  );
  const [validUntil, setValidUntil] = useState(institution.validUntil.slice(0, 10));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const studentSeats = parseSeats(students);
  const problems = licenceProblems({ tier, teacherSeats: seats, studentSeats });
  const fits = Object.keys(problems).length === 0;
  const ready = fits && reason.trim().length >= 10 && !busy;

  return (
    <div className="max-w-xl">
      <h2 className="font-display text-lg text-ink">{t("institution.licence.title")}</h2>
      <p className="mt-2 text-sm text-ink-soft">{t("institution.licence.body")}</p>

      <div className="mt-6 flex flex-col gap-4">
        <SelectField
          labelKey="institution.licence.tier"
          value={tier}
          onChange={(event) => setTier(event.target.value as LicenceTier)}
          options={TIERS.map((value) => ({ value, labelKey: `tier.${value}` }))}
        />
        <TextField
          labelKey="institution.licence.seats"
          type="number"
          min={1}
          max={TIER_MAX_TEACHERS[tier]}
          value={String(seats)}
          errorKey={problems.teacherSeats}
          errorParams={{ tier }}
          onChange={(event) => setSeats(Number(event.target.value))}
        />
        <TextField
          labelKey="institution.licence.students"
          type="number"
          required
          min={1}
          max={STUDENT_SEATS_MAX}
          value={students}
          hintKey={
            institution.studentSeats === undefined && students.trim() === ""
              ? "institution.licence.studentsUnset"
              : undefined
          }
          errorKey={students.trim() === "" ? undefined : problems.studentSeats}
          errorParams={{ max: formatNumber(locale, STUDENT_SEATS_MAX) }}
          onChange={(event) => setStudents(event.target.value)}
        />
        <TextField
          labelKey="institution.licence.validUntil"
          type="date"
          value={validUntil}
          onChange={(event) => setValidUntil(event.target.value)}
        />
        <TextField
          labelKey="audit.reason"
          hintKey="audit.reasonHint"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />

        <div>
          <Button
            busy={busy}
            disabled={!ready}
            onClick={async () => {
              setBusy(true);
              try {
                await call(CALLABLES.setLicenceManually, {
                  institutionId: institution.id,
                  licenceTier: tier,
                  teacherSeats: seats,
                  studentSeats,
                  validUntil: new Date(`${validUntil}T00:00:00.000Z`).toISOString(),
                  reason: reason.trim(),
                });
                success("institution.licence.applied");
                setReason("");
              } catch (error) {
                showError(error);
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("institution.licence.apply")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Usage({ institution }: { institution: Institution }) {
  const t = useT();
  const { rows } = useInstitutionUsers<User>(institution.id);

  const counts = useMemo(() => {
    const teachers = rows.filter((user) => user.role === "teacher" || user.role === "owner");
    const students = rows.filter((user) => user.role === "student");
    return { teachers: teachers.length, students: students.length };
  }, [rows]);

  /*
    Two limits, each beside what it is measured against. The owner is counted
    as a teacher here, as the backend counts them: they hold a seat.
  */
  return (
    <dl className="grid max-w-md grid-cols-[16rem_1fr] gap-y-2 text-sm">
      <dt className="text-ink-muted">{t("institution.usage.teacherSeats")}</dt>
      <dd className="text-ink">{institution.teacherSeats}</dd>
      <dt className="text-ink-muted">{t("institution.usage.teachersUsed")}</dt>
      <dd className="text-ink">{counts.teachers}</dd>
      <dt className="text-ink-muted">{t("institution.usage.studentSeats")}</dt>
      <dd className="text-ink">
        {institution.studentSeats === undefined
          ? t("institution.usage.unlimited")
          : institution.studentSeats}
      </dd>
      <dt className="text-ink-muted">{t("institution.usage.studentsUsed")}</dt>
      <dd className="text-ink">{counts.students}</dd>
    </dl>
  );
}

/**
 * Everyone in the tenant.
 *
 * Their access, and nothing they wrote. A scene, a note, a score — none of it is
 * reachable from here, and the line under the table says so rather than leaving
 * an admin to wonder whether they simply have not found it yet.
 */
function Users({ institution }: { institution: Institution }) {
  const t = useT();
  const { rows, loading } = useInstitutionUsers<User>(institution.id);

  if (loading) return <p className="text-sm text-ink-muted">{t("common.loading")}</p>;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full max-w-3xl border-collapse text-sm">
          <caption className="sr-only">{t("nav.users")}</caption>
          <thead>
            <tr className="border-b border-paper-deep text-left text-ink-muted">
              <th scope="col" className="px-3 py-2.5 font-medium">{t("institution.users.column.name")}</th>
              <th scope="col" className="px-3 py-2.5 font-medium">{t("institution.users.column.role")}</th>
              <th scope="col" className="px-3 py-2.5 font-medium">{t("institution.users.column.username")}</th>
              <th scope="col" className="px-3 py-2.5 font-medium">{t("institution.users.column.status")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((user) => (
              <tr key={user.uid} className="border-b border-paper-warm last:border-0">
                <td className="px-3 py-3 text-ink">{user.displayName}</td>
                <td className="px-3 py-3 text-ink-muted">{user.role}</td>
                <td className="px-3 py-3 text-ink-muted">{user.username}</td>
                <td className="px-3 py-3 text-ink-muted">
                  {user.disabled ? t("institutions.state.suspended") : t("institutions.state.active")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 max-w-2xl text-sm text-ink-muted">{t("institution.users.noContent")}</p>
    </div>
  );
}

/**
 * What this institution has accepted, and what is stopping it.
 *
 * This used to print "Everything that applies has been accepted" and print it
 * unconditionally — it read nothing. On 2026-09-09 an admin looking at a
 * studio that could not legally start a lesson, waiting on two documents
 * Spontar has never published, was given a clean bill of health here while the
 * Legal library called the same studio blocked two clicks away.
 *
 * The same coverage the library is built on answers it now: whose move it is,
 * what is outstanding, and — when it is ours — that nobody at the institution
 * can do anything about it.
 */
function Legal({ institution }: { institution: Institution }) {
  const t = useT();
  const coverage = useCallable<{ rows: CoverageRow[] }>(CALLABLES.getLegalCoverage);
  const row = coverage.data?.rows.find((entry) => entry.institutionId === institution.id);

  if (coverage.loading) {
    return <p className="text-sm text-ink-muted">{t("common.loading")}</p>;
  }
  if (!row) {
    return <p className="max-w-2xl text-sm text-ink-muted">{t("institution.legal.unknown")}</p>;
  }

  const fault = faultOf(row);

  return (
    <div className="max-w-2xl">
      <p className={`text-sm ${FAULT_TONE[fault]}`}>
        {t(
          fault === "ours"
            ? "institution.legal.ours"
            : fault === "theirs"
              ? "institution.legal.theirs"
              : "institution.legal.clear",
          { count: fault === "ours" ? row.gaps.length : row.outstanding.length }
        )}
      </p>

      {row.gaps.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1 text-sm text-ink-muted">
          {row.gaps.map((type) => (
            <li key={type}>{t(`legal.type.${type}`)}</li>
          ))}
        </ul>
      ) : null}

      {row.outstanding.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1 text-sm text-ink-muted">
          {row.outstanding.map((document) => (
            <li key={document.docId}>
              {document.title}
              <span className="ml-2 text-ink-faint">
                {t("institution.legal.version", { version: document.version })}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {row.blockedGroups > 0 ? (
        <p className="mt-3 text-sm text-accent">
          {t("institution.legal.blockedGroups", { count: row.blockedGroups })}
        </p>
      ) : null}

      <Link to="/legal/coverage" className="mt-4 inline-block text-sm text-cue-green underline">
        {t("institution.legal.toCoverage")}
      </Link>
      <span className="sr-only">{institution.name}</span>
    </div>
  );
}

/**
 * The end of the road.
 *
 * The export is offered above the deletion rather than beside it, because the
 * order is the point: an institution that leaves is entitled to what it made,
 * and afterwards there is nothing to take.
 */
function Danger({ institution }: { institution: Institution }) {
  const t = useT();
  const navigate = useNavigate();
  const { success, showError } = useToast();
  const [suspending, setSuspending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);

  const isSuspended = institution.suspendedAt !== undefined;

  return (
    <div className="max-w-2xl">
      <section>
        <h2 className="font-display text-lg text-ink">{t("institution.danger.exportFirst")}</h2>
        <p className="mt-2 text-sm text-ink-soft">{t("institution.danger.exportBody")}</p>
        <Button
          variant="secondary"
          busy={busy}
          className="mt-3"
          onClick={async () => {
            setBusy(true);
            try {
              const data = await call<{ institutionId: string }, Record<string, unknown>>(
                CALLABLES.exportInstitution,
                { institutionId: institution.id }
              );
              const blob = new Blob([JSON.stringify(data, null, 2)], {
                type: "application/json",
              });
              const url = URL.createObjectURL(blob);
              const anchor = document.createElement("a");
              anchor.href = url;
              anchor.download = `${institution.id}.json`;
              anchor.click();
              URL.revokeObjectURL(url);
              success("institution.danger.exported");
            } catch (error) {
              showError(error);
            } finally {
              setBusy(false);
            }
          }}
        >
          {t("institution.danger.exportFirst")}
        </Button>
      </section>

      <section className="mt-10 border-t border-paper-deep pt-6">
        <Button variant="secondary" onClick={() => setSuspending(true)}>
          {t(isSuspended ? "institution.danger.reactivate" : "institution.danger.suspend")}
        </Button>

        <Button variant="danger" className="ml-3" onClick={() => setDeleting(true)}>
          {t("institution.danger.delete")}
        </Button>
      </section>

      <ConfirmDialog
        open={suspending}
        onClose={() => setSuspending(false)}
        onConfirm={async (reason) => {
          await call(CALLABLES.suspendInstitution, {
            institutionId: institution.id,
            suspended: !isSuspended,
            reason,
          });
          success(isSuspended ? "institution.danger.reactivated" : "institution.danger.suspended");
        }}
        titleKey="institution.danger.suspendTitle"
        titleParams={{ name: institution.name }}
        bodyKey="institution.danger.suspendBody"
        expected={institution.name}
        confirmKey="institution.danger.suspendConfirm"
        mismatchKey="institution.danger.mismatch"
        withReason
        destructive={!isSuspended}
      />

      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={async (reason) => {
          await call(CALLABLES.deleteInstitution, {
            institutionId: institution.id,
            reason,
          });
          // Logged from here as well as by the backend: the console's own entry
          // records who pressed the button in which browser, and the two
          // together are what a colleague reads in a year.
          success("institution.danger.deleted", { name: institution.name });
          navigate("/institutions", { replace: true });
        }}
        titleKey="institution.danger.deleteTitle"
        titleParams={{ name: institution.name }}
        bodyKey="institution.danger.deleteBody"
        expected={institution.name}
        confirmKey="institution.danger.deleteConfirm"
        mismatchKey="institution.danger.mismatch"
        withReason
      />
    </div>
  );
}
